import { useIsAdmin, useTasksOnly } from '@/hooks/use-scopes';
import AdminOnly from '@/components/ui/AdminOnly';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, CheckSquare, Clock, ExternalLink,
  Flame, HardDrive, Layers, Loader2, CheckCircle2, Send, Sparkles, TrendingDown, TrendingUp, Users as UsersIcon,
} from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { toast } from 'sonner';
import {
  qk, fetchClients, fetchStrategies, fetchMyCapacity, fetchDashboard, fetchBackupStatus,
  requestBackup, fetchTrackSummary, type DashboardBucket,
} from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';
import { ConnectWhatsAppNudge } from '@/components/connect/MyChannelsCard';
import { cn } from '@/lib/cn';
import { dubai as callTime, fetchMeetings, MEETING_STATUS } from '@/lib/calls';

const PERIODS: Array<[string, string]> = [
  ['7d',     'Last 7 days'],
  ['30d',    'Last 30 days'],
  ['90d',    'Last 90 days'],
  ['all',    'All time'],
  ['custom', 'Custom range'],
];

function today(): string { return new Date().toISOString().slice(0, 10); }
function daysAgo(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function iso(d: Date): string { return d.toISOString().slice(0, 10); }
function startOfMonth(offsetMonths = 0): string {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offsetMonths);
  return iso(d);
}
function endOfMonth(offsetMonths = 0): string {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offsetMonths + 1); d.setDate(0);
  return iso(d);
}
// YYYY-MM-DD with a plausible year (2020-2099) - guards against partial keystrokes
const SANE_DATE = /^20\d{2}-\d{2}-\d{2}$/;

const QUICK_RANGES: Array<{ label: string; start: () => string; end: () => string }> = [
  { label: 'This month',    start: () => startOfMonth(0),  end: () => today() },
  { label: 'Last month',    start: () => startOfMonth(-1), end: () => endOfMonth(-1) },
  { label: 'This quarter',  start: () => { const d = new Date(); const q = Math.floor(d.getMonth() / 3) * 3; d.setMonth(q); d.setDate(1); return iso(d); }, end: () => today() },
  { label: 'Year to date',  start: () => iso(new Date(new Date().getFullYear(), 0, 1)), end: () => today() },
];

const STAGE_COLOR: Record<string, string> = {
  brief:        'bg-slate-500',
  shoot:        'bg-blue-500',
  raws_review:  'bg-yellow-500',
  edit:         'bg-purple-500',
  dual_review:  'bg-orange-500',
  done:         'bg-emerald-500',
  posted:       'bg-accent-400',
};

export default function DashboardPage() {
  const { user } = useAuth();
  // Owner/manager: the whole picture. Clinic-wide staff: their clinics' funnel
  // and activity (the API scopes it). Own-tasks people: their own work only;
  // company analytics would be noise and the team roster is not theirs to see.
  const isAdmin = useIsAdmin();
  const tasksOnly = useTasksOnly();
  const showAnalytics = tasksOnly === false;
  const [period, setPeriod] = useState('30d');
  const [clientId, setClientId] = useState('');
  const [strategyId, setStrategyId] = useState('');
  // Custom-range state. Initialized to "last 30 days" so the picker is sensible
  // the moment the user switches to Custom. Updates are debounced via TanStack
  // Query's reactive keys - no manual debounce needed.
  const [customStart, setCustomStart] = useState(daysAgo(30));
  const [customEnd, setCustomEnd]     = useState(today());

  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients });
  const strategies = useQuery({ queryKey: qk.strategies, queryFn: fetchStrategies });
  const cap = useQuery({ queryKey: ['my-capacity'], queryFn: fetchMyCapacity });
  // Only feed the custom dates into the query when they look sensible. Without
  // this guard the dashboard refetches on every keystroke (year "0", "00", "020"
  // etc) and the backend throws 500 on partial input.
  const customValid = SANE_DATE.test(customStart) && SANE_DATE.test(customEnd) && customStart <= customEnd;
  const effStart = period === 'custom' && customValid ? customStart : '';
  const effEnd   = period === 'custom' && customValid ? customEnd   : '';
  const dash = useQuery({
    queryKey: ['dashboard', period, clientId, strategyId, effStart, effEnd],
    queryFn: () => fetchDashboard(period === 'custom' && !customValid ? '30d' : period, clientId, strategyId, effStart, effEnd),
    enabled: showAnalytics,
  });

  // Strategy options narrow if a client is picked
  const strategyOpts = (strategies.data ?? [])
    .filter((s) => !clientId || s.client_id === clientId);

  const greeting = user?.display_name?.split(' ')[0] || 'there';
  const d = dash.data;
  const myUtil = cap.data?.utilization_pct ?? 0;

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${greeting}`}
        subtitle={showAnalytics ? (d ? `Showing ${d.period_label === 'all' ? 'all time' : d.period_label} · ${d.period_start} to ${d.period_end}` : ' ') : 'Your work at a glance.'}
      />
      <ConnectWhatsAppNudge name={user?.display_name ?? ''} />

      {/* Filter bar */}
      {showAnalytics && <Card className="mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select label="Period" value={period} onChange={setPeriod}
            options={PERIODS.map(([v, l]) => ({ value: v, label: l }))} />
          <Select label="Client" value={clientId} onChange={(v) => { setClientId(v); setStrategyId(''); }} searchable
            options={[
              { value: '', label: 'All clients' },
              ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]} />
          <Select label="Strategy" value={strategyId} onChange={setStrategyId} searchable
            options={[
              { value: '', label: 'All strategies' },
              ...strategyOpts.map((s) => ({ value: s.id, label: s.title, hint: s.code })),
            ]} />
        </div>

        {/* Custom-range sub-row: full-width, only renders when Custom is picked.
            Two date inputs + quick presets so the user doesn't need to type for
            common ranges. Refetch only fires when both dates parse cleanly. */}
        {period === 'custom' && (
          <div className="mt-3 pt-3 border-t border-line space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <Input label="Start date" type="date" value={customStart}
                max={customEnd || today()}
                onChange={(e) => setCustomStart(e.target.value)} />
              <Input label="End date" type="date" value={customEnd}
                min={customStart} max={today()}
                onChange={(e) => setCustomEnd(e.target.value)} />
              <div className="text-xs text-muted whitespace-nowrap pb-2.5">
                {customValid
                  ? <>Range: <span className="text-muted-strong font-mono">{customStart} -&gt; {customEnd}</span></>
                  : <span className="text-amber-400">Pick valid dates</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-muted mr-1">Quick:</span>
              {QUICK_RANGES.map(q => (
                <button
                  key={q.label}
                  onClick={() => { setCustomStart(q.start()); setCustomEnd(q.end()); }}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-line text-muted hover:text-muted-strong hover:border-accent-400/50 transition"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>}

      <FlowCard />

      {/* Personal capacity strip */}
      <Card className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2">
            <Activity size={14} className="text-accent-400" /> My capacity
          </h2>
          <span className={cn(
            'text-sm font-mono',
            myUtil > 100 ? 'text-red-400' : myUtil > 80 ? 'text-yellow-400' : 'text-emerald-400'
          )}>{cap.data?.weekly_capacity ? `${myUtil}%` : 'no target set'}</span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className={cn(
            'h-full transition-all',
            myUtil > 100 ? 'bg-red-500/70' : myUtil > 80 ? 'bg-yellow-500/70' : 'bg-emerald-500/60'
          )} style={{ width: `${Math.min(myUtil, 100)}%` }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
          <Stat label="My open tasks"  value={cap.data?.open_tasks ?? 0} />
          <Stat label="My capacity"    value={cap.data?.weekly_capacity ?? 0} />
          <Stat label="My overdue"     value={cap.data?.overdue_tasks ?? 0} accent={(cap.data?.overdue_tasks ?? 0) > 0 ? 'red' : undefined} />
          <Stat label="Done last 7d"   value={cap.data?.completed_last_7d ?? 0} accent="green" />
        </div>
      </Card>

      <CallsWaitingCard userId={user?.id} />
      {/* Offline-backup-drive health: an owner concern, and the endpoint is admin-only. */}
      {isAdmin && <BackupCard />}

      {showAnalytics && dash.isLoading && <div className="text-muted text-sm py-8 text-center">Loading...</div>}

      {d && (
        <>
          {/* Throughput KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {d.throughput.map((k) => <KpiTile key={k.label} kpi={k} />)}
          </div>

          {/* Stage funnel + Mixes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
            <Card className="lg:col-span-2">
              <h3 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2 mb-3">
                <Layers size={14} className="text-accent-400" /> Stage funnel
                <span className="text-[10px] text-muted ml-auto">all time, current state of every creative</span>
              </h3>
              <FunnelChart buckets={d.stage_funnel} />
              <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-800 text-xs">
                <Stat label="Total creatives"    value={d.creatives_total} />
                <AdminOnly inline><Stat label="Avg revision round" value={d.avg_revision_round} /></AdminOnly>
                <Stat label="Done in window"     value={d.creatives_done} accent="green" />
              </div>
            </Card>

            <Card>
              <h3 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-accent-400" /> Type mix
              </h3>
              <BarList buckets={d.type_mix} emptyHint="No creatives in this window." />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
            <Card>
              <h3 className="text-sm font-medium text-muted-strong mb-3">Funnel mix</h3>
              <BarList buckets={d.funnel_mix} emptyHint="Nothing yet." />
            </Card>
            <Card>
              <h3 className="text-sm font-medium text-muted-strong mb-3">Channels in use</h3>
              <BarList buckets={d.channel_mix} emptyHint="No channels assigned yet." />
            </Card>
            <Card>
              <h3 className="text-sm font-medium text-muted-strong mb-3">Top talents (this window)</h3>
              {d.top_talents.length === 0 ? (
                <div className="text-xs text-muted py-2">No talent attributions yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {d.top_talents.map((t) => (
                    <li key={t.talent_id} className="flex items-center justify-between text-sm">
                      <span className="text-muted-strong truncate">{t.talent_name}</span>
                      <span className="text-xs text-accent-400 font-mono shrink-0">×{t.creative_count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* Hot capacity (team roster, admin only) + Upcoming postings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            {isAdmin && <Card>
              <h3 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2 mb-3">
                <Flame size={14} className="text-orange-400" /> Hot capacity
                <span className="text-[10px] text-muted ml-auto">utilization ≥ 80% or has overdue</span>
              </h3>
              {d.hot_users.length === 0 ? (
                <div className="text-xs text-muted py-2">Everyone's healthy. ✨</div>
              ) : (
                <ul className="space-y-2">
                  {d.hot_users.map((u) => (
                    <li key={u.user_id}>
                      <Link to={`/admin/users/${u.user_id}`} className="block hover:bg-slate-800/40 rounded-lg p-2 -m-2 transition">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm text-muted-strong truncate">{u.display_name}</div>
                            <div className="text-[10px] text-muted">
                              {u.designations.slice(0,2).join(', ').replace(/_/g, ' ')}
                              {u.overdue_tasks > 0 && <span className="text-red-400 ml-2 inline-flex items-center gap-1"><AlertTriangle size={9} />{u.overdue_tasks} overdue</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={cn('text-sm font-mono',
                              u.utilization_pct > 100 ? 'text-red-400' : u.utilization_pct > 80 ? 'text-yellow-400' : 'text-emerald-400')}>
                              {u.utilization_pct}%
                            </div>
                            <div className="text-[10px] text-muted">{u.open_tasks}/{u.weekly_capacity || '-'}</div>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>}

            <Card>
              <h3 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2 mb-3">
                <Send size={14} className="text-accent-400" /> Upcoming postings
                <span className="text-[10px] text-muted ml-auto">next 14 days</span>
              </h3>
              {d.upcoming_postings.length === 0 ? (
                <div className="text-xs text-muted py-2">Nothing scheduled.</div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {d.upcoming_postings.map((u) => (
                    <li key={u.creative_id}>
                      <Link to={`/creatives/${u.creative_id}`} className="block py-2 hover:text-accent-400">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-mono text-accent-400">{u.code}</div>
                            <div className="text-sm text-muted-strong truncate">{u.title}</div>
                            <div className="text-[10px] text-muted truncate">{u.client_name} · {u.creative_type}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-xs text-muted-strong">{u.posting_date}</div>
                            <div className="text-[10px] text-muted">{daysUntil(u.posting_date)}</div>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* Recent activity */}
          <Card>
            <h3 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2 mb-3">
              <Clock size={14} className="text-accent-400" /> Recent activity
              <span className="text-[10px] text-muted ml-auto">last 25 events in window</span>
            </h3>
            {d.recent_activity.length === 0 ? (
              <div className="text-xs text-muted py-2">No activity in this window.</div>
            ) : (
              <ol className="relative border-l border-slate-800 ml-2 pl-4 space-y-2">
                {d.recent_activity.map((a) => (
                  <li key={a.id} className="text-xs">
                    <span className="absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full bg-accent-400 border-2 border-bg-card" />
                    <span className="text-accent-400">{a.actor_name}</span>{' '}
                    <span className="text-muted">{a.verb.replace(/_/g, ' ')}</span>
                    {a.target_title && <> <span className="text-muted-strong">{a.target_title}</span></>}
                    <div className="text-[10px] text-muted">{relTime(a.at)}</div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

const BACKUP_CFG = {
  healthy: { dot: 'bg-emerald-500', text: 'text-emerald-400', ring: 'border-emerald-500/30', label: 'Online' },
  stale:   { dot: 'bg-yellow-500',  text: 'text-yellow-400',  ring: 'border-yellow-500/30',  label: 'Stale' },
  offline: { dot: 'bg-red-500',     text: 'text-red-400',     ring: 'border-red-500/30',     label: 'Offline' },
} as const;

/** Phase -> what a person actually wants to read. Keyed to the phases the
 *  runner emits in backup.ps1 / backup.sh. */
const PHASE_LABEL: Record<string, string> = {
  start: 'Starting backup',
  media: 'Syncing media from Google Cloud',
  media_done: 'Media in sync',
  db_dump: 'Dumping the database',
  verify: 'Checking the dump reads back',
  download: 'Downloading the dump',
  rotate: 'Tidying old dumps',
  complete: 'Backup complete',
};

function BackupCard() {
  // Poll every minute: the runner checks in every 15, and presence is the thing
  // people watch after plugging the drive in. 5 minutes felt broken.
  const qc = useQueryClient();
  // Poll every 3s while a backup is running so the bar actually moves, and back
  // off to a minute when nothing is happening.
  const q = useQuery({
    queryKey: ['backup-status'],
    queryFn: fetchBackupStatus,
    refetchInterval: (query) => (query.state.data?.run_active ? 3_000 : 60_000),
  });
  const ask = useMutation({
    mutationFn: requestBackup,
    onSuccess: () => {
      toast.success('Backup requested. The connected machine picks it up within 15 minutes.');
      qc.invalidateQueries({ queryKey: ['backup-status'] });
    },
    onError: () => toast.error('Could not request a backup'),
  });
  const b = q.data;
  if (!b) return null;

  // Keep the finished state on screen for a few minutes so a backup that ends
  // while nobody is looking still shows "complete" rather than vanishing.
  const finishedAgoMin = b.run_finished_at
    ? (Date.now() - new Date(b.run_finished_at).getTime()) / 60_000
    : Infinity;
  const justFinished = !b.run_active && !!b.run_phase && finishedAgoMin < 10;
  const runOk = b.run_status === 'ok';

  const cfg = BACKUP_CFG[b.health];
  const never = !b.last_run_at;
  const when = never ? '' : relTime(b.last_run_at!);
  const problem = b.status === 'fail' || b.status === 'partial';

  let msg: string;
  if (b.run_active) {
    // Say what is happening rather than how old the last one is. A big sync runs
    // for hours, and "last backup 26 hours ago" reads as neglect while the drive
    // is in fact busy.
    msg = `Backing up now${b.device_host ? ' on ' + b.device_host : ''}${b.run_detail ? ': ' + b.run_detail : ''}.`;
  } else if (never) {
    msg = 'No backup has run yet. Connect the KAF-Backup drive to a laptop to start the daily backup.';
  } else if (b.health === 'healthy') {
    msg = `Last backup ${when}${b.host ? ' from ' + b.host : ''}. The drive is connected and backing up.`;
  } else if (b.health === 'stale') {
    msg = problem
      ? `Last backup ${when} reported problems (${b.status}). Check the drive's backup log.`
      : `Last backup ${when}. Plug the KAF-Backup drive into a laptop soon.`;
  } else {
    msg = `Last backup ${when}. The drive looks disconnected - plug it into a laptop.`;
  }

  return (
    <Card className={cn('mb-5 border', cfg.ring)}>
      <div className="flex items-center gap-3">
        <HardDrive size={18} className={cn('shrink-0', cfg.text)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('w-2 h-2 rounded-full shrink-0', cfg.dot, b.health === 'healthy' && 'animate-pulse')} />
            <h2 className="text-sm font-medium text-muted-strong">Offline backup drive</h2>
            <span className={cn('text-[11px] font-semibold uppercase tracking-wider', cfg.text)}>{cfg.label}</span>
          </div>
          <p className="text-xs text-muted mt-0.5">{msg}</p>
        </div>
        {!never && (
          <div className="hidden sm:flex gap-5 text-right shrink-0">
            <div>
              <div className="text-[10px] text-muted uppercase tracking-wider">Media files</div>
              <div className="text-sm text-muted-strong font-mono">{b.media_files ?? '-'}</div>
            </div>
            <div>
              <div className="text-[10px] text-muted uppercase tracking-wider">DB dumps</div>
              <div className="text-sm text-muted-strong font-mono">{b.db_dumps ?? '-'}</div>
            </div>
          </div>
        )}
      </div>

      {/* Progress of a run in flight, and the outcome of the one just finished.
          Phase labels rather than a bare percentage, so it is clear WHAT is
          taking time when a large media sync is the slow part. */}
      {(b.run_active || justFinished) && (
        <div className="mt-3 pt-3 border-t border-slate-800">
          <div className="flex items-center gap-2 mb-1.5">
            {b.run_active ? (
              <Loader2 size={13} className="text-accent-400 animate-spin shrink-0" />
            ) : (
              <CheckCircle2 size={13} className={cn('shrink-0', runOk ? 'text-emerald-400' : 'text-red-400')} />
            )}
            <span className="text-xs font-medium text-muted-strong">
              {b.run_active ? (PHASE_LABEL[b.run_phase ?? ''] ?? 'Backing up') : (runOk ? 'Backup complete' : 'Backup failed')}
            </span>
            <span className="text-[11px] text-muted font-mono ml-auto tabular-nums">
              {b.run_active ? `${b.run_percent ?? 0}%` : (b.run_finished_at ? relTime(b.run_finished_at) : '')}
            </span>
          </div>
          <div
            className="h-1.5 rounded-full bg-slate-800 overflow-hidden"
            role="progressbar"
            aria-valuenow={b.run_percent ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Backup progress"
          >
            <div
              className={cn(
                'h-full rounded-full transition-all duration-700 ease-out',
                b.run_active ? 'bg-accent-400' : (runOk ? 'bg-emerald-400' : 'bg-red-400'),
              )}
              style={{ width: `${b.run_active ? (b.run_percent ?? 0) : 100}%` }}
            />
          </div>
          {b.run_detail && (
            <p className="text-[11px] text-muted mt-1.5 truncate" title={b.run_detail}>{b.run_detail}</p>
          )}
        </div>
      )}

      {/* Live presence + the manual trigger. Deliberately separate from the
          freshness badge above: a drive can be connected right now with the
          next backup not due for hours, and those are different facts. */}
      <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-3 flex-wrap">
        <span className={cn('w-2 h-2 rounded-full shrink-0', b.drive_present ? 'bg-emerald-400' : 'bg-slate-600')} />
        <span className="text-xs text-muted-strong">
          {b.drive_present
            ? <>Drive connected{b.drive_label ? <> &middot; {b.drive_label}</> : null}{b.device_host ? <> on {b.device_host}</> : null}
                {b.free_gb != null ? <> &middot; {Math.round(b.free_gb).toLocaleString()} GB free</> : null}</>
            : b.device_online
              ? <>{b.device_host || 'A machine'} is online but the drive is not plugged in</>
              : <>No machine has checked in{b.minutes_since_seen != null ? ` for ${Math.round(b.minutes_since_seen)} min` : ''}</>}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {b.pending_request && (
            <span className="text-[11px] text-amber-400">
              Backup requested{b.pending_requested_by ? ` by ${b.pending_requested_by}` : ''}, waiting for the machine
            </span>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => ask.mutate()}
            disabled={ask.isPending || b.pending_request || !b.drive_present}
            title={!b.drive_present ? 'Plug the backup drive in first' : 'Run a backup now'}
          >
            {ask.isPending ? 'Requesting...' : 'Back up now'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FunnelChart({ buckets }: { buckets: DashboardBucket[] }) {
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <div className="space-y-2">
      {buckets.map((b) => {
        const pct = (b.count / max) * 100;
        return (
          <div key={b.key} className="flex items-center gap-3">
            <div className="text-xs text-muted w-28 shrink-0 text-right">{b.label}</div>
            <div className="flex-1 h-7 rounded-md bg-slate-900 relative overflow-hidden border border-slate-800">
              <div className={cn('h-full rounded-md transition-all', STAGE_COLOR[b.key] ?? 'bg-slate-600')} style={{ width: `${pct}%`, opacity: 0.55 }} />
              <div className="absolute inset-0 flex items-center justify-end pr-2 text-xs text-muted-strong font-mono">{b.count}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BarList({ buckets, emptyHint }: { buckets: DashboardBucket[]; emptyHint?: string }) {
  if (buckets.length === 0) return <div className="text-xs text-muted py-2">{emptyHint}</div>;
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <ul className="space-y-1.5">
      {buckets.map((b) => {
        const pct = (b.count / max) * 100;
        return (
          <li key={b.key} className="text-xs">
            <div className="flex items-baseline justify-between mb-0.5">
              <span className="text-muted-strong capitalize">{b.label.replace(/_/g, ' ')}</span>
              <span className="text-muted font-mono">{b.count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-accent-400/70" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function KpiTile({ kpi }: { kpi: { label: string; value: number; delta_pct?: number | null } }) {
  const d = kpi.delta_pct;
  const positive = d != null && d > 0;
  const negative = d != null && d < 0;
  return (
    <Card>
      <div className="text-[10px] text-muted uppercase tracking-wider">{kpi.label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-3xl font-semibold text-muted-strong">{kpi.value}</span>
        {d != null && (
          <span className={cn(
            'text-[11px] font-mono inline-flex items-center gap-0.5',
            positive ? 'text-emerald-400' : negative ? 'text-red-400' : 'text-muted'
          )}>
            {positive && <TrendingUp size={11} />}
            {negative && <TrendingDown size={11} />}
            {d > 0 ? '+' : ''}{d}%
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted mt-0.5">vs previous period</div>
    </Card>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: 'red' | 'green' }) {
  const color = accent === 'red' ? 'text-red-400' : accent === 'green' ? 'text-emerald-400' : 'text-muted-strong';
  return (
    <div>
      <div className="text-[10px] text-muted uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-semibold mt-0.5', color)}>{value}</div>
    </div>
  );
}

function daysUntil(iso: string): string {
  const ms = new Date(iso + 'T00:00:00Z').getTime() - Date.now();
  const d = Math.ceil(ms / 86_400_000);
  if (d < 0) return `${-d}d ago`;
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  return `in ${d}d`;
}
function relTime(iso: string): string {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

/** Production flow at a glance: what the open creatives are waiting on. */
function FlowCard() {
  const q = useQuery({ queryKey: ['track', 'summary', ''], queryFn: () => fetchTrackSummary(), refetchInterval: 60000 });
  const d = q.data;
  if (!d) return null;
  const top = Object.entries(d.by_next).sort((a, b) => b[1] - a[1]).slice(0, 5);
  return (
    <Card className="mb-5">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-medium text-muted-strong inline-flex items-center gap-2"><CheckSquare size={14} className="text-accent-400" /> Production flow</h2>
        <Link to="/tracker" className="text-[11px] text-accent-400 hover:underline">Open the tracker</Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
        <Stat label="Open creatives" value={d.total_open} />
        <Stat label="Blocked" value={d.blocked} accent={d.blocked ? 'red' : undefined} />
        <Stat label="Waiting for a doctor" value={d.by_next.doctor ?? 0} accent={d.waiting_doctor_oldest_days > 2 ? 'red' : undefined} />
        <Stat label="Shoots next 7 days" value={d.shoots_next_7} accent={d.shoots_no_crew || d.on_shoot_not_ready ? 'red' : 'green'} />
      </div>
      {top.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {top.map(([k, n]) => (
            <Link key={k} to={`/tracker?next=${k}`} className="text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border border-slate-800 text-muted hover:text-muted-strong hover:border-accent-400/40 inline-flex items-center gap-1">
              <span className="font-mono text-accent-400">{n}</span> {d.labels[k] ?? k}
            </Link>
          ))}
        </div>
      )}
      {(d.shoots_no_crew > 0 || d.on_shoot_not_ready > 0) && (
        <div className="text-[11px] text-amber-300 mt-2">{[d.shoots_no_crew ? `${d.shoots_no_crew} shoot${d.shoots_no_crew === 1 ? '' : 's'} without crew` : '', d.on_shoot_not_ready ? `${d.on_shoot_not_ready} creative${d.on_shoot_not_ready === 1 ? '' : 's'} on a shoot without an approved script` : ''].filter(Boolean).join(' · ')}</div>
      )}
    </Card>
  );
}


/** Script calls waiting on this person: audio to upload, or outcomes to confirm. A
 *  transcript that nobody confirms is a doctor's answer that never reaches the team. */
function CallsWaitingCard({ userId }: { userId?: string }) {
  const q = useQuery({ queryKey: ['meetings', 'open', 'mine'], queryFn: () => fetchMeetings({ open: true }), refetchInterval: 60_000 });
  const mine = (q.data ?? []).filter((m) => m.organizer_id === userId && (m.status === 'review' || m.status === 'awaiting_audio'));
  if (!mine.length) return null;
  return (
    <Card className="mb-5 border border-amber-500/30" data-testid="calls-waiting">
      <h2 className="text-sm font-medium text-muted-strong mb-2">Script calls waiting on you</h2>
      <div className="space-y-1.5">
        {mine.map((m) => (
          <Link key={m.id} to={`/meetings/${m.id}`} className="flex flex-wrap items-center gap-2 text-sm hover:text-accent-400">
            <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', MEETING_STATUS[m.status].cls)}>
              {m.status === 'review' ? `${m.creatives.filter((c) => !c.filed_at).length} to confirm` : 'upload audio'}
            </span>
            <span className="text-muted-strong">{m.title}</span>
            {m.starts_at && <span className="text-xs text-muted">{callTime(m.starts_at)}</span>}
          </Link>
        ))}
      </div>
    </Card>
  );
}
